import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';
import { BehaviorSubject, interval, Subject } from 'rxjs';
import { FiVimeoService } from '@fi-shared/media-bank/fi-vimeo/fi-vimeo.service';
import { FiBrowserEventsService } from '@fi-shared/fi-shared-services/fi-browser-events.service';
import { debounce, takeUntil } from 'rxjs/operators';
import { isDefined } from '@fi-shared/utils';
import { FiVimeoDataService, FiVimeoVideoStatus } from './fi-vimeo-data.service';
import { comparator } from '@fi-shared/utils/array-sort-comparators';
import { FiVimeoPreviewPicture } from '@fi-shared/media-bank/fi-vimeo/fi-vimeo';
import { FiImageSize } from '@fi-shared/types';
import { FiMediaStorageService } from '@fi-shared/media-bank/fi-media-storage.service';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { VimeoUnavailablePlaceholderSize } from '@fi-shared/media-bank/fi-vimeo/fi-vimeo-enum';

// https://chainformation.atlassian.net/wiki/spaces/FI/pages/1796669449/Video+player
export enum FiVimeoPreviewSize {
    Large,
    Normal,
    Medium,
    Small,
    XS,
}

@Component({
    selector: 'fi-vimeo-player',
    template: `
        @if (loading$ | async) {
            <div class="text-center">
                <fi-loading class="small" />
            </div>
        } @else {
            @if (showPreview) {
                <div
                    class="fi-vimeo-player-preview-container"
                    [class.editor-mode]="editorMode"
                    (click)="openVideoOverlay($event)"
                >
                    <img
                        class="fi-vimeo-player-preview-image"
                        (load)="setPreviewIconFontSize()"
                        [src]="previewUrl$ | async"
                    />
                    <div #previewIcon class="fi-vimeo-player-preview-icon">
                        <mat-icon class="fas fa-play-circle" />
                    </div>
                </div>
            }

            @if ((videoStatus$ | async) !== FiVimeoVideoStatus.Ok) {
                <fi-vimeo-unavailable-video-handler [placeholderSize]="placeholderSize" />
            }
        }
    `,
    styleUrls: ['./fi-vimeo-player.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-vimeo-player' },
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class FiVimeoPlayerComponent implements OnInit, OnDestroy {
    @Input() previewSize: FiVimeoPreviewSize;
    @Input() isPreviewActionButtonsShown: boolean = true;
    @Input() editorMode: boolean = false;
    @Input() disableMediaViewerDialog: boolean = false;

    @Input() mediaBankFileReference: IMediaBankFileReference;
    @Input() useDefaultVimeoPreview: boolean;

    @Input() placeholderSize: VimeoUnavailablePlaceholderSize = VimeoUnavailablePlaceholderSize.Small;

    @Output() load = new EventEmitter<void>();
    @Output() openMediaViewer = new EventEmitter();
    @Output() vimeoStatusChange = new EventEmitter<FiVimeoVideoStatus>();

    private previewIconElemRef: ElementRef<HTMLDivElement>;
    @ViewChild('previewIcon', { static: false }) set previewIconElementRef(elementRef: ElementRef<HTMLDivElement>) {
        if (elementRef) this.previewIconElemRef = elementRef;
    }

    @HostBinding('class.embed-responsive')
    @HostBinding('class.embed-responsive-16by9')
    get showPreview(): boolean {
        return this.videoStatus$.value === FiVimeoVideoStatus.Ok;
    }

    private safeUrl: SafeResourceUrl;
    protected videoStatus$ = new BehaviorSubject<FiVimeoVideoStatus>(FiVimeoVideoStatus.Processing);
    protected loading$ = new BehaviorSubject<boolean>(true);
    protected previewUrl$ = new BehaviorSubject<string>(undefined);

    protected readonly FiVimeoVideoStatus = FiVimeoVideoStatus;

    constructor(
        private vimeoService: FiVimeoService,
        private vimeoDataService: FiVimeoDataService,
        private elementRef: ElementRef<HTMLElement>,
        private renderer2: Renderer2,
        private fiBrowserEventsService: FiBrowserEventsService,
        private mediaStorageService: FiMediaStorageService,
    ) {}

    ngOnInit(): void {
        this.vimeoStatusChange.emit(this.videoStatus$.value);
        this.safeUrl = this.vimeoService.getVimeoSafeUrl(this.mediaBankFileReference.PhysicalFileName);
        this.vimeoDataService
            .getVimeoData$(this.mediaBankFileReference.PhysicalFileName)
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((videoInfo) => {
                // turn off loading
                if (this.loading$.value) {
                    this.loading$.next(false);
                }
                if (videoInfo.status === FiVimeoVideoStatus.Ok) {
                    this.load.next();
                    this.videoStatus$.next(FiVimeoVideoStatus.Ok);
                    this.vimeoStatusChange.emit(this.videoStatus$.value);

                    // place previews with 4:3 ratio to the end
                    const previewPictures = (videoInfo.previewPictures || []).sort(
                        comparator((item: FiVimeoPreviewPicture) => {
                            if (item.width / item.height < 1.77) {
                                return 10000;
                            }
                            return item.width;
                        }),
                    );
                    setTimeout(() => {
                        // we need setTimeout because this.elementRef.nativeElement has no correct width at start
                        if (previewPictures.length) {
                            const width = this.elementRef.nativeElement.getBoundingClientRect().width;
                            let previewPicture = previewPictures.find((item) => item.width >= width);
                            if (!previewPicture) {
                                previewPicture = previewPictures[videoInfo.previewPictures.length - 1];
                            }
                            this.previewUrl$.next(previewPicture.link);
                        }
                    });
                }
            });

        this.fiBrowserEventsService
            .getWindowEventSubject$('resize')
            .pipe(
                takeUntil(this.ngOnDestroy$),
                debounce(() => interval(100)),
            )
            .subscribe(() => this.setPreviewIconFontSize());

        if ('ResizeObserver' in window) {
            const resizeObserver = new window.ResizeObserver(() => {
                this.setPreviewIconFontSize();
            });
            resizeObserver.observe(this.elementRef.nativeElement);
        }

        this.setPreviewImageSizeClass();
    }

    private setPreviewImageSizeClass(): void {
        if (isDefined(this.previewSize)) {
            this.renderer2.addClass(
                this.elementRef.nativeElement,
                `vimeo-preview-` + FiVimeoPreviewSize[this.previewSize],
            );
        }
    }

    private getMediaBankFileRefUrl(): string {
        if (this.useDefaultVimeoPreview) {
            // when replace vimeo in MediaBankEditComponent we have no vimeo preview on upcloud object storage while we will not click 'Save' button
            return this.mediaStorageService.getDefaultVimeoPreview();
        }

        return this.mediaStorageService.getMediaFileUrlByFileName(
            this.mediaBankFileReference.PhysicalFileName + this.mediaBankFileReference.Extension,
            FiImageSize.Large,
            this.mediaBankFileReference.ChangedOn,
        );
    }

    protected openVideoOverlay($event: MouseEvent): void {
        if (this.disableMediaViewerDialog) return;

        this.openMediaViewer.emit();
        $event.stopPropagation();
    }

    protected setPreviewIconFontSize(): void {
        if (isDefined(this.previewIconElemRef)) {
            const previewIconHeight = this.previewIconElemRef.nativeElement.getBoundingClientRect().height;
            const previewIconWidth = this.previewIconElemRef.nativeElement.getBoundingClientRect().width;
            this.renderer2.setStyle(
                this.previewIconElemRef.nativeElement,
                'fontSize',
                `${previewIconWidth > previewIconHeight ? previewIconHeight : previewIconWidth}px`,
            );
        }
    }

    private ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    protected readonly VimeoUnavailablePlaceholderSize = VimeoUnavailablePlaceholderSize;
}
